import { takeLatest, put, select, call } from "redux-saga/effects";
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import omit from 'lodash/fp/omit';
import compact from 'lodash/compact';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import _first from 'lodash/first';
import dynamicGalleryApi from '../../api/dynamicGalleryApi';
import galleryApi from '../../api/galleryApi';
import config from '../../config';
import { GET_GALLERY_WITH_FALLBACKS } from "../../actions/actionTypes";
import safeSaga from "../safeSaga";
import { skuMapSelector } from '../../selectors/skuMap/skuMapSelector';
import {
  configuratorSelector,
  productDetailsSelector,
  gallerySelector,
  productSelector,
  productInfoSelector,
} from "../../selectors";
import gallerySearchTokenGenerator from "../../utils/gallerySearchTokenGenerator";
import { updateGallery, parseHarmonyGallery } from "../../actions/getGallery";

function* isFullyVisualized(primaryImage) {
  const isFullyVisualizedRegEx = new RegExp('^([0-9]{5,9})');
  return isFullyVisualizedRegEx.test(primaryImage);
}

function* generateGallerySearchToken(skuMap, product, choices, productId, options, visualizedSiteProductOptions) {
  const token = gallerySearchTokenGenerator(
    skuMap,
    product,
    choices,
    productId,
    options,
    visualizedSiteProductOptions
  );

  // console.log(`Token: ${token}`);

  return token;
}

function* getDynamicGalleryImage(productInfo, galleryData) {
  const attachmentItemIds = flow(
    omit(['DefaultProduct', 'primary']),
    map(p => p.itemId),
  )(productInfo) || [];

  const itemIds = compact([get(productInfo, 'primary.itemId'), ...attachmentItemIds.slice(0, 4)]);

  if (!isEmpty(itemIds)) {
    try {
      // check if calling dynamic gallery url gives an image
      const { status } = yield call(dynamicGalleryApi, itemIds);
      if (status === 200) {
        return [{
        id: 'dynamicGalleryImage',
        sizes: ["145", "600", "1000"],
        type: 'IMAGE',
        url: `${config.customBaseUrl}/pipfigurator/api/dynamicgallery?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>`,
        primaryLink: `${config.customBaseUrl}/pipfigurator/api/dynamicgallery?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>`,
        thumbnail: `${config.customBaseUrl}/pipfigurator/api/dynamicgallery?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>`,
        source: "THD_DYNAMIC_GALLERY",
        }, ...galleryData];
      }
    } catch (err) {
      console.log(err);
      return galleryData;
    }
  }

  // console.log(`Gallery Data: ${galleryData}`);

  return galleryData;
}

function* getGallery() {
  let galleryAssetData;

  try {
    const product = yield select(productSelector);
    const skuMap = yield select(skuMapSelector);
    const { choices, options } = yield select(configuratorSelector);
    const {
      visualizedSiteProductOptions,
      productId,
      attributes: {
        dynamicGallery,
      },
    } = yield select(productDetailsSelector);

    if (typeof (productId) === 'undefined') return;

    const searchToken = yield call(generateGallerySearchToken, skuMap, product, choices, productId, options, visualizedSiteProductOptions);

    const folder = product?.details?.attributes?.scene7Folder;

    if (!folder) return;

    const gallery = yield call(galleryApi, folder, searchToken);
    const galleryData = gallery.assets.map(x => {
      const id = x.s3ObjectName;
      const url = x.primaryLink.replace("hei=1000", "hei=<SIZE>") || x.primaryLink.replace("hei=100", "hei=<SIZE>");
      const videoStill = x.mediaType === 'VIDEO' ? x.thumbnail : null;
      const sizes = ["145", "600", "1000"];

      return {
        id,
        sizes,
        type: x.mediaType,
        url,
        source: "CONFIGURABLE_S3",
        videoStill,
        primaryLink: x.primaryLink,
        thumbnail: x.thumbnail,
      };
    });

    galleryAssetData = galleryData;

    const productInfo = yield select(productInfoSelector);

    if (dynamicGallery === 'Yes') {
      const fullGallery = yield call(getDynamicGalleryImage, productInfo, galleryAssetData)
      yield put(updateGallery({assets: fullGallery}));
    } else {
      if (galleryData.length) {
        const hasFullVisualization = yield call(
          isFullyVisualized,
          searchToken,
        );

        if (hasFullVisualization) {
          yield put(updateGallery({assets: galleryAssetData}));
        } else {
          const fullGallery = yield call(getDynamicGalleryImage, productInfo, galleryAssetData);
          yield put(updateGallery({ assets: fullGallery }));
        }
      }
    }

    // console.log(`Gallery Data: ${JSON.stringify(galleryData)}`);
  } catch (err) {
    yield put(updateGallery({ assets: galleryAssetData }))
  } finally {
    const { assets } = yield select(gallerySelector);
    // console.log(`Assets: ${JSON.stringify(assets)}`);
    if (assets.length > 0) {
      yield put(parseHarmonyGallery(assets));
    }
  }
}

export default function* gallerySaga() {
  // console.log('Running saga');
  yield takeLatest(GET_GALLERY_WITH_FALLBACKS, safeSaga(getGallery));
}
